import React from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const inputClass =
  "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100";
const textAreaClass =
  "min-h-28 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100";

const Field = ({ label, children, wide }) => (
  <label className={`block ${wide ? "md:col-span-2" : ""}`}>
    <span className="text-sm font-semibold text-slate-700">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const ModelSelect = ({ value, onChange }) => (
  <select name="model" value={value || ""} onChange={onChange} className={inputClass}>
    <option value="">Select Model</option>
    <optgroup label="Speaking Models">
      <option value="readaloud">Read Aloud</option>
      <option value="repeatsentence">Repeat Sentence</option>
      <option value="describeimage">Describe Image</option>
      <option value="retelllecture">Retell Lecture</option>
      <option value="answershortquestion">Answer Short Question</option>
    </optgroup>
    <optgroup label="Writing Models">
      <option value="summarizetext">Summarize Text</option>
      <option value="writeessay">Write Essay</option>
    </optgroup>
    <optgroup label="Reading Models">
      <option value="singleanswer">Single Answer</option>
      <option value="doubleanswer">Double Answer</option>
      <option value="reorderparagraph">Reorder Paragraph</option>
      <option value="fillintheblank">Fill in the Blank</option>
    </optgroup>
    <optgroup label="Listening Models">
      <option value="summarizespokentext">Summarize Spoken Text</option>
      <option value="writefromdictation">Write from Dictation</option>
      <option value="highlightsummary">Highlight Correct Summary</option>
      <option value="missingword">Select Missing Word</option>
      <option value="incorrectword">Highlight Incorrect Word</option>
    </optgroup>
  </select>
);

export default function AddQuestionForm({ formData, handleInputChange, handleAddNewQuestion }) {
  const handleAddQuestion = async () => {
    try {
      const response = await fetch("/api/admin/questions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: formData.model,
          questionData: formData,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Question added successfully!", { position: "top-right", autoClose: 3000 });
        handleAddNewQuestion();
      } else {
        toast.error(`Error adding question: ${data.error}`, { position: "top-right", autoClose: 5000 });
      }
    } catch (error) {
      toast.error(`Server error: ${error.message}`, { position: "top-right", autoClose: 5000 });
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />

      <div className="mb-5">
        <h3 className="text-lg font-bold text-slate-950">Add Question</h3>
        <p className="text-sm text-slate-500">Create a new practice question and attach optional media metadata.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Select Model" wide>
          <ModelSelect value={formData.model} onChange={handleInputChange} />
        </Field>

        <Field label="ID*">
          <input type="text" name="id" value={formData.id || ""} onChange={handleInputChange} className={inputClass} />
        </Field>

        <Field label="Q Title">
          <input type="text" name="q_title" value={formData.q_title || ""} onChange={handleInputChange} className={inputClass} />
        </Field>

        <Field label="Title">
          <input type="text" name="title" value={formData.title || ""} onChange={handleInputChange} className={inputClass} />
        </Field>

        <Field label="Media Type">
          <input type="text" name="media_type" value={formData.media_type || ""} onChange={handleInputChange} className={inputClass} />
        </Field>

        <Field label="Media Link">
          <input type="text" name="media_link" value={formData.media_link || ""} onChange={handleInputChange} className={inputClass} />
        </Field>

        <Field label="Image Link">
          <input type="text" name="image_link" value={formData.image_link || ""} onChange={handleInputChange} className={inputClass} />
        </Field>

        <Field label="Question">
          <textarea name="question" value={formData.question || ""} onChange={handleInputChange} className={textAreaClass} />
        </Field>

        <Field label="Answer">
          <input type="text" name="answer" value={formData.answer || ""} onChange={handleInputChange} className={inputClass} />
        </Field>

        <Field label="Audio Script">
          <textarea name="audio_script" value={formData.audio_script || ""} onChange={handleInputChange} className={textAreaClass} />
        </Field>

        <Field label="Complexity">
          <input
            type="number"
            name="complexity"
            value={formData.complexity || ""}
            onChange={handleInputChange}
            className={inputClass}
          />
        </Field>

        <label className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
          <input
            type="checkbox"
            name="is_weekly_prediction"
            checked={formData.is_weekly_prediction === 1}
            onChange={handleInputChange}
            className="h-5 w-5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
          />
          <span className="text-sm font-semibold text-slate-700">Weekly prediction</span>
        </label>
      </div>

      <div className="mt-5 flex justify-end">
        <button
          onClick={handleAddQuestion}
          className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700"
        >
          Add Question
        </button>
      </div>
    </section>
  );
}
